import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
// import VatNumberStep from './components/VatNumberStep';
import {
  commercialCompanyDataSchema,
  commercialDefaultValues,
  commercialTechnicalDataSchema,
  personalDataSchema,
  vatNumberValidationSchema,
} from './utils';
import { generalAlertError } from '../../common/utils/alerts';
import CommercialCompanyStep from './components/CommercialCompanyStep';
import { commercialInitialData } from '../../features/forms/formsActions';
import TechnicalDataStep from './components/TechnicalDataStep';
import PersonalDataStep from './components/PersonalDataStep';
import {
  commercialReportReset,
  getCommercialReport,
  getCommercialReportForQuestionare,
  manualQuote,
  updateReportVatStatus,
} from '../../features/commercialReport/commercialReportActions';
import generateFiscalCode from '../../common/helpers/generateFiscalCode';
import { modifyRequestData } from '../../services/axios-client/axeCommons';
import ProgressBar from '../../common/componentsV2/ProgressBar';
import { FastQuoteLoading } from '../../common/components/elements/FastQuoteLoading';
import { chatAxe } from '../../services/axios-client/chatAxeClient';
import { questionarioCommercial } from './Questionario/questions';
import QuestionarioDataStep from './components/QuestionarioDataStep';
import { useValidationSchema } from './components/QuestionarioSchema.validations';
import { validateProduct95Complete, validateProduct95Guarantees } from './validations/validationFunctions';
import {
  commercialStepFieldsById,
  NON_TOUCHABLE_FIELDS,
  isCommercialFieldValid,
  getBlockingFields,
  buildMissingFieldsMessage,
} from './validations/commercialStepFields';
import { V2FontImport } from '../../common/formInputsV2/V2Fonts';
import { FormLayout } from '../../common/formInputsV2/FormLayout.styles';
import { FooterNav } from '../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton, SecondaryButton } from '../../common/formInputsV2/Button.styles';
import { sonnerErrorAlert } from '../../common/utils/sonnerAlerts';
import QuoteGenerateLoading from '../../common/componentsV2/elements/QuoteGenerateLoading';

const StepIdType = {
  COMMERCIAL_COMPANY: 'commercial_company',
  TECHNICAL_DATA: 'technical_data',
  PERSONAL_DATA: 'personal_data',
  QUESTIONARIO: 'questionario',
};

export function NewCommercialScreen() {
  const location = useLocation();
  const [loadingQuestionare, setLoadingQuestionare] = useState(false);
  const [aiQuotestionare, setAiQuotestionare] = useState([]);
  const [completeQuestionario, setCompleteQuestionario] = useState(null);
  const [isModifyState, setIsModifyState] = useState(false);
  const [showPrivacyErrors, setShowPrivacyErrors] = useState(false);

  const { isSubmitting, request_token } = useSelector(
    (store) => store.commercialReport.manual_quote,
  );
  const { loadingVatData, commercial_report, isVatCalled } = useSelector(
    (store) => store.commercialReport.report,
  );

  const { municipalities, states, dataPrivacy } = useSelector(
    (store) => store.forms.commercial,
  );

  const validationSchema = useValidationSchema(aiQuotestionare);
  const {
    register,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    watch,
    control,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(
      [
        vatNumberValidationSchema,
        commercialCompanyDataSchema,
        commercialTechnicalDataSchema,
        personalDataSchema,
        validationSchema,
      ].reduce((acc, schema) => acc.concat(schema)),
    ),
    shouldFocusError: false,
    defaultValues: commercialDefaultValues,
  });

  const formData = watch();

  const getCommercialActivityCode = async (attecoCode, attecoDescription) => {
    setLoadingQuestionare(true);

    try {
      const res = await chatAxe.post('/broker-ai/commerciale-ateco', {
        attecoCode,
        attecoDescription,
      });

      if (
        res.status !== 200
        || !res.data?.data?.categoriesWithQuestions?.length
      ) {
        setAiQuotestionare(questionarioCommercial);
        return;
      }

      const normalizedQuestionnaire = res.data.data.categoriesWithQuestions.map(
        (category) => ({
          title: category.title,
          questions: category.questions.map((q) => ({
            question: q.question,
            input_name: q.input_name,
          })),
        }),
      );

      setAiQuotestionare(normalizedQuestionnaire);
    } catch (error) {
      setAiQuotestionare(questionarioCommercial);
    } finally {
      setLoadingQuestionare(false);
    }
  };

  const updateFormData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  const privacyItemChangeHandler = (value, item) => {
    if (!value.target.checked) {
      const newArray = [...formData.privacies];
      const index = newArray.indexOf(item.id);
      newArray.splice(index, 1);
      updateFormData({
        privacies: newArray,
      });
      return;
    }
    const answersNewArray = [...formData.privacies];
    answersNewArray.push(item.id);
    updateFormData({
      privacies: answersNewArray,
    });
  };

  const isValid = (name) => isCommercialFieldValid(errors, touchedFields, name);

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector(
        '.is-invalid, [aria-invalid="true"], .general-input-error, .invalid-feedback.d-block',
      );
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const autoValidatePrefilledFields = async (fields) => {
    const filled = (fields || []).filter((name) => {
      const value = watch(name);
      return value !== undefined && value !== null && value !== '';
    });

    if (filled.length === 0) return;

    filled
      .filter((name) => !NON_TOUCHABLE_FIELDS.includes(name))
      .forEach((name) => {
        setValue(name, watch(name), { shouldTouch: true, shouldValidate: false });
      });

    await trigger(filled);
  };

  const steps = [
    {
      id: StepIdType.COMMERCIAL_COMPANY,
      title: 'Completa i Dati',
    },
    {
      id: StepIdType.TECHNICAL_DATA,
      title: 'Dati Tecnici',
    },
    {
      id: StepIdType.PERSONAL_DATA,
      title: 'Dati Contatto',
    },
    {
      id: StepIdType.QUESTIONARIO,
      title: 'Questionario',
    },
  ];

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const step = steps[currentStepIndex];

  useEffect(() => {
    autoValidatePrefilledFields(commercialStepFieldsById[step.id] || []);
  }, [currentStepIndex]);

  const renderStepComponent = () => {
    switch (step.id) {
      case StepIdType.COMMERCIAL_COMPANY:
        return (
          <CommercialCompanyStep
            register={register}
            errors={errors}
            isValid={isValid}
            setValue={setValue}
            control={control}
            formData={formData}
            isVatCalled={isVatCalled}
            isModifyState={isModifyState}
          />
        );
      case StepIdType.TECHNICAL_DATA:
        return (
          <TechnicalDataStep
            register={register}
            watch={watch}
            errors={errors}
            isValid={isValid}
            setValue={setValue}
            control={control}
          />
        );
      case StepIdType.PERSONAL_DATA:
        return (
          <PersonalDataStep
            register={register}
            watch={watch}
            errors={errors}
            isValid={isValid}
            setValue={setValue}
            control={control}
            formData={formData}
            dataPrivacy={dataPrivacy}
            privacyItemChangeHandler={privacyItemChangeHandler}
            showPrivacyErrors={showPrivacyErrors}
          />
        );
      case StepIdType.QUESTIONARIO:
        return (
          <QuestionarioDataStep
            aiQuotestionare={aiQuotestionare}
            loadingQuestionare={loadingQuestionare}
            register={register}
            errors={errors}
            setCompleteQuestionario={setCompleteQuestionario}
            completeQuestionario={completeQuestionario}
            setValue={setValue}
          />
        );
      default:
        return null;
    }
  };

  const validateDataPrivacy = () => {
    let { error, message } = '';

    dataPrivacy.forEach((privacy) => {
      if (privacy.required === 1) {
        const userDataArray = { ...formData }; // make a separate copy of the object
        const index = userDataArray.privacies.indexOf(privacy.id);

        if (index < 0) {
          error = true;
          message = 'Tutti i campi sono obbligatori! Tutta la privacy con la stella deve essere controllata';
        }
      }
    });
    return { error, message };
  };

  const handleNextStep = () => {
    let stepValidation = null;
    let stepFields = {};
    switch (step.id) {
      case StepIdType.VAT_NUMBER: {
        stepValidation = vatNumberValidationSchema;
        stepFields = Object.keys(vatNumberValidationSchema.fields);
        break;
      }
      case StepIdType.COMMERCIAL_COMPANY: {
        stepValidation = commercialCompanyDataSchema;
        stepFields = commercialStepFieldsById.commercial_company;
        break;
      }
      case StepIdType.TECHNICAL_DATA: {
        stepValidation = commercialTechnicalDataSchema;
        stepFields = commercialStepFieldsById.technical_data;
        break;
      }
      case StepIdType.PERSONAL_DATA: {
        stepValidation = personalDataSchema;
        stepFields = commercialStepFieldsById.personal_data;
        break;
      }
      case StepIdType.QUESTIONARIO: {
        stepValidation = validationSchema;
        stepFields = Object.keys(validationSchema.fields);
        break;
      }
      default: {
        sonnerErrorAlert('Step non riconosciuto.');
        return;
      }
    }

    if (stepValidation) {
      const isValidStep = stepValidation.isValidSync(formData);
      if (!isValidStep) {
        const blocking = getBlockingFields(stepValidation, formData, stepFields);
        trigger(stepFields, { shouldFocus: true }).then(() => {
          sonnerErrorAlert(buildMissingFieldsMessage(blocking));
          scrollToFirstError();
        });
        return;
      }
    }

    // Guarantee-dependency validation only makes sense once the basic
    // technical fields are actually filled in — tpab_value in particular
    // is what lets NobisCoveragesSection auto-add FABB/RILO and satisfy
    // most of these dependencies before the user ever sees this error.
    // Runs after the generic step validation above, not before it.
    if (step.id === StepIdType.TECHNICAL_DATA) {
      const validationResult = validateProduct95Guarantees(formData, {
        assetNumber: 1,
      });

      if (validationResult.hasBlockingErrors()) {
        const errorMessages = validationResult.errors
          .map((err) => `• ${err.message}`)
          .join('\n');

        generalAlertError(
          `Correggere i seguenti errori prima di procedere:\n\n${errorMessages}`,
        );
        return;
      }

      // Show warnings but allow to continue
      if (validationResult.hasWarnings()) {
        // eslint-disable-next-line no-console
        console.warn('Validation warnings:', validationResult.warnings);
      }
    }

    if (step.id === StepIdType.PERSONAL_DATA) {
      const validation = validateDataPrivacy();
      if (validation.error) {
        setShowPrivacyErrors(true);
        sonnerErrorAlert(validation.message);
        scrollToFirstError();
        return;
      }
    }

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Final submission - perform complete validation
      const finalValidation = validateProduct95Complete(formData, {
        validateAssets: false,
        assetNumber: 1,
      });

      if (finalValidation.hasBlockingErrors()) {
        const errorMessages = finalValidation.errors
          .map((err) => `• ${err.message}`)
          .join('\n');

        sonnerErrorAlert(
          `Impossibile procedere con la quotazione. Errori trovati:\n\n${errorMessages}`,
        );
        return;
      }

      const validation = validateDataPrivacy();
      if (validation.error) {
        setShowPrivacyErrors(true);
        sonnerErrorAlert(validation.message);
        scrollToFirstError();
        return;
      }

      if (formData.gender != 'G') {
        const d = formData.date_of_birth.split('-');

        const fData = {
          name: formData.name,
          surname: formData.surname,
          gender: formData.gender,
          day: d[2],
          month: d[1],
          year: d[0],
        };

        if (formData.born_abroad) {
          fData.birthplace = states.filter(
            (s) => s.state_code === formData.country_of_birth_code,
          )[0]?.name;
          fData.birthplaceProvincia = 'EE';
        } else {
          fData.birthplace = municipalities.filter(
            (municipality) => municipality.cadastral_code === formData.commune_of_birth_code,
          )[0]?.name;
          fData.birthplaceProvincia = formData.province_of_birth_code;
        }
        formData.fiscal_code = generateFiscalCode(fData);
      }
      delete formData.attpr_value;
      manualQuote(formData);
    }
  };

  useEffect(() => {
    if (commercial_report !== null) {
      setValue('vat_number', commercial_report.commercial_company.vat_number);
      setValue(
        'turnover_value',
        commercial_report.commercial_company.turnover_value,
      );
      setValue(
        'company_name',
        commercial_report.commercial_company.business_name,
      );
      setValue(
        'number_of_employees',
        commercial_report.commercial_company.alter_summary_employees_nr,
      );
      setValue(
        'commercial_company_id',
        commercial_report.commercial_company.id,
      );

      // Same reason as the modify-flow load below: plain setValue() here
      // doesn't touch/validate anything, so these fields would otherwise
      // stay neutral (no green) until the user manually interacts with them.
      autoValidatePrefilledFields(commercialStepFieldsById.commercial_company);
    }
  }, [commercial_report]);

  useEffect(() => {
    if (commercial_report !== null && aiQuotestionare.length === 0) {
      getCommercialActivityCode(commercial_report?.commercial_company?.activity_code, commercial_report?.commercial_company?.activity_description);
    }
  }, [commercial_report, aiQuotestionare]);

  useEffect(() => {
    commercialReportReset();
    commercialInitialData();
  }, []);

  useEffect(() => {
    if (request_token !== null && request_token !== undefined) {
      window.location.href = `/quotes/commercial/${request_token}`;
    }
  }, [request_token]);

  const getCommercialDataFromVat = () => {
    trigger('vat_number', { shouldFocus: true }).then((isFieldValid) => {
      if (isFieldValid && currentStepIndex === 0) {
        getCommercialReport(formData.vat_number);
      } else {
        sonnerErrorAlert('Per favore, inserisci una Partita IVA valida.');
        scrollToFirstError();
      }
    });
  };

  useEffect(() => {
    const callAsync = async () => {
      if (
        location.state !== null
        && location.state.requestToken !== undefined
      ) {
        setIsModifyState(true);
        const {
          data: { data },
        } = await modifyRequestData(location.state.requestToken);

        // ? Cleanup questionario data
        delete data?.location_and_structures;
        delete data?.infrastructure_and_machines;
        delete data?.security_and_logistics;
        delete data?.product_and_its;
        delete data?.finance_and_personals;
        // ? No need to use this just for old quotes
        delete data?.site_address;

        setValue('attpr_value', data?.technical_location_data?.attpr_value);
        Object.keys(data).forEach((key) => {
          setValue(key, data[key], {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        });

        if (data.vat_number) {
          const resData = await getCommercialReportForQuestionare(data.vat_number);

          if (resData.success && aiQuotestionare.length === 0) {
            getCommercialActivityCode(resData?.data?.commercial_company?.activity_code, resData?.data?.commercial_company?.activity_description);
          }
        }

        // The setValue calls above only touch top-level keys (e.g. 'address'),
        // not the leaf paths (e.g. 'address.address') isValid()/red-green
        // actually check — mark every known field across all steps touched +
        // validated now, regardless of which step is currently showing.
        await autoValidatePrefilledFields(
          Object.values(commercialStepFieldsById).flat(),
        );

        setIsModifyState(false);
      }
    };

    callAsync();
  }, []);

  useEffect(() => {
    if (formData.technical_location_data.annoc_value) {
      setValue('technical_location_data.annor_value', formData.technical_location_data.annoc_value);
    }
  }, [formData.technical_location_data.annoc_value]);

  useEffect(() => {
    if (formData.attpr_value) {
      setValue('technical_location_data.attpr_value', formData.attpr_value);
    }
  }, [formData.attpr_value]);

  // useEffect(() => {
  //   if (
  //     formData.vat_number !== ''
  //     && (location.state === null || location.state.requestToken == undefined)
  //   ) {
  //     getCommercialReport(formData.vat_number);
  //   }
  // }, [formData.vat_number]);

  useEffect(() => {
    const vatIsValid = isValid('vat_number');

    if (!isVatCalled && formData.vat_number !== '' && vatIsValid && currentStepIndex === 0) {
      updateReportVatStatus(true);
    }
  }, [isVatCalled, formData.vat_number]);

  const stepsQuote = [
    'Dati Aziendali',
    'Dati Tecnici',
    'Dati Contatto',
    'Questionario',
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [currentStepIndex]);

  if (isSubmitting === true) {
    return <QuoteGenerateLoading countNr={45} />;
  }

  return (
    <>
      <V2FontImport />
      <div className="d-flex align-content-center justify-content-center">
        <ProgressBar activeStep={currentStepIndex + 1} steps={stepsQuote} />
      </div>

      {loadingVatData && <FastQuoteLoading />}

      <FormLayout className="form-container">
        {renderStepComponent()}
      </FormLayout>
      {currentStepIndex === 0 && !isVatCalled ? (
        <FooterNav className="footer-buttons">
          <div className="d-flex justify-content-between">
            <PrimaryButton
              className="btn btn-questionnaire"
              type="button"
              onClick={() => getCommercialDataFromVat()}
            >
              Cerca Dati PIVA
            </PrimaryButton>
          </div>
        </FooterNav>
      ) : (
        <FooterNav className="footer-buttons">
          <div className="d-flex justify-content-between">
            {step.id !== StepIdType.COMMERCIAL_COMPANY && (
              <SecondaryButton
                type="button"
                className="btn btn-questionnaire"
                onClick={() => {
                  if (currentStepIndex > 0) {
                    setCurrentStepIndex(currentStepIndex - 1);
                  }
                }}
              >
                Back
              </SecondaryButton>
            )}
            <PrimaryButton
              className="btn btn-questionnaire"
              type="button"
              onClick={() => handleNextStep()}
            >
              Continua
            </PrimaryButton>
          </div>
        </FooterNav>
      )}

      {/* {isSubmitting === true && <CommercialQuoteLoading />} */}
    </>
  );
}
